import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// constants
import { Modals } from 'constants/index'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// hooks
import { useShowModal } from 'hooks'
import { useCurrentUser } from 'hooks'
// types
import { IReview, ReviewStatus, SecurityProfileReviewPermission } from 'types/graphql'
// graphql
import { REVIEW_DELETE_MUTATION } from 'graphql/reviews/mutations'
// utils
import map from 'lodash/fp/map'
import pipe from 'lodash/fp/pipe'
import isFunction from 'lodash/isFunction'
import { safeGetOr } from 'utils/fp'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { generateReviews, getRefetchQueriesForReviews } from 'utils/reviews'

// types
export declare namespace NReviewsListBL {
  interface IContext {
    canCreate: boolean
    canDelete: boolean
    canModify: boolean
    handleDeleteReview: (reviewId: string) => void
    handleStartReview: (review: IReview & any) => void
    handleShowReviewDetails: (review: IReview & any) => void
    handleEditScheduledReview: (review: IReview & any) => void
    myScheduledReviewsFilter: (scheduledReview: IReview & any) => boolean
    entity: any
    reviews: IReview[] & any
    isSidebar: boolean
  }

  interface IIncomingProps {
    canCreate?: boolean
    canDelete?: boolean
    canModify?: boolean
    canViewCompleted?: string
    canViewInProgress?: string
    entity: any
    hideModal?: () => void
    isFullModal?: boolean
    isSidebar?: boolean
    children?: React.ReactNode
  }
}

export const ReviewsListBLContext = React.createContext<NReviewsListBL.IContext>({} as any)

const ReviewsListBL: React.FunctionComponent<NReviewsListBL.IIncomingProps> = ({
  canCreate = false,
  canDelete = false,
  canModify = false,
  children,
  isSidebar,
  entity,
  isFullModal,
  hideModal,
  canViewInProgress = SecurityProfileReviewPermission.None,
  canViewCompleted = SecurityProfileReviewPermission.None
}) => {
  const [deleteReviewMutation] = useMutation(REVIEW_DELETE_MUTATION)

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  // redux
  const currentUser = useCurrentUser()

  const { showModal: showReviewCreateModal } = useShowModal({ modalType: Modals.REVIEW_CREATE_MODAL })
  const { showModal: showReviewDetailsModal } = useShowModal({ modalType: Modals.REVIEW_DETAILS_MODAL })
  const { showModal: showSidebarReviewDetailsModal } = useShowModal({ modalType: Modals.SIDEBAR_REVIEW_DETAILS_MODAL })
  const { showModal: showScheduledReviewEditModal } = useShowModal({ modalType: Modals.SCHEDULE_REVIEW_MODAL })
  const { showModal: showConfirmDeleteModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_REVIEW_MODAL })

  // constants
  const reviews = generateReviews({ entity }).filter((review) => {
    const { scheduledReview, status } = review

    if (status === ReviewStatus.InProgress) {
      if (canViewInProgress === SecurityProfileReviewPermission.None) {
        return
      } else if (canViewInProgress === SecurityProfileReviewPermission.Own) {
        return scheduledReview.scheduledReviewers.some(({ userId, positionId }) =>
          userId ? userId === currentUser.id : positionId === currentUser.id
        )
      }
    }

    if (status === ReviewStatus.Complete) {
      if (canViewCompleted === SecurityProfileReviewPermission.None) {
        return
      } else if (canViewCompleted === SecurityProfileReviewPermission.Own) {
        return scheduledReview.scheduledReviewers.some(({ userId, positionId }) =>
          userId ? userId === currentUser.id : positionId === currentUser.id
        )
      }
    }

    return review
  })

  // methods
  const myScheduledReviewsFilter = (scheduledReview) => {
    const myPositionIds = pipe<any, any, any>(safeGetOr([], 'positions'), map('id'))(currentUser)

    const reviewers = safeGetOr<any, any, any>([], 'scheduledReview.scheduledReviewers')(scheduledReview)

    return reviewers.some((reviewer) => {
      return reviewer.userId === currentUser.id || (reviewer.positionId && myPositionIds.includes(reviewer.positionId))
    })
  }

  const handleStartReview = (review) => {
    showReviewCreateModal({ entity, review })

    if (isFullModal && isFunction(hideModal)) {
      hideModal()
    }
  }

  const handleEditScheduledReview = (review) =>
    showScheduledReviewEditModal({ isEdit: true, entity, review, canDelete })

  const handleShowReviewDetails = (review) => {
    if (isSidebar) {
      return showSidebarReviewDetailsModal({ entity, review })
    }
    showReviewDetailsModal({ entity, review })
  }

  const handleDeleteReview = (reviewId) => {
    const refetchQueries = getRefetchQueriesForReviews(entity)

    showConfirmDeleteModal({
      width: 'sm',
      handleConfirm: async () => {
        try {
          if (isFunction(treeQueryRefetch)) {
            await treeQueryRefetch()
          }

          await deleteReviewMutation({
            variables: { id: reviewId },
            refetchQueries
          })
        } catch (errors) {
          console.log('[ReviewsListBL.handleDeleteReview]: ', parseGraphQLErrorMessages(errors))
        }
      }
    })
  }

  const value = {
    canCreate,
    canDelete,
    canModify,
    entity,
    handleDeleteReview,
    handleEditScheduledReview,
    handleShowReviewDetails,
    handleStartReview,
    isSidebar,
    myScheduledReviewsFilter,
    reviews
  }

  return <ReviewsListBLContext.Provider value={value}>{children}</ReviewsListBLContext.Provider>
}

export default ReviewsListBL
